/**
 * Laravel API Service
 * 
 * Handles communication with the Laravel backend for:
 * - Restaurant info
 * - Menu data
 * - Order submission (via Cloud Functions)
 */

const API_BASE_URL = process.env.VUE_APP_LARAVEL_API_URL || 'https://api.dandero.no'

/**
 * Fetch restaurant info by slug
 * @param {string} slug - Restaurant cleanurl/slug
 * @returns {Promise<Object>} Restaurant data
 */
export async function fetchRestaurantInfo(slug) {
  const response = await fetch(`${API_BASE_URL}/webservice/v1/table-ordering/restaurants/${slug}`)
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Restaurant not found or table ordering not enabled')
    }
    throw new Error(`Failed to fetch restaurant: ${response.status}`)
  }
  
  return response.json()
}

/**
 * Fetch menu for a restaurant
 * @param {string} slug - Restaurant cleanurl/slug
 * @returns {Promise<Object>} Menu data with categories and items
 */
export async function fetchMenu(slug) {
  const response = await fetch(`${API_BASE_URL}/webservice/v1/table-ordering/restaurants/${slug}/menu`)
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Menu not found')
    }
    throw new Error(`Failed to fetch menu: ${response.status}`)
  }
  
  return response.json()
}

/**
 * Transform Laravel API menu format to Vue app format
 * 
 * Laravel format:
 * {
 *   categories: [
 *     { id, name, sizeVariants, items: [...] }
 *   ]
 * }
 * 
 * Vue format:
 * {
 *   items: { categoryId: [...items] },
 *   categories: { categoryId: "Category Name" }
 * }
 * 
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Transformed menu data
 */
export function transformMenuData(apiResponse) {
  const items = {}
  const categories = {}
  const categoryMeta = {} // Store additional category metadata

  // Handle different response structures
  const categoriesArray = apiResponse.categories || apiResponse.data?.categories || []

  for (const category of categoriesArray) {
    const categoryId = `cat_${category.id}`
    
    // Store category name
    categories[categoryId] = category.name
    
    // Store category metadata (size variants, alcohol flag, etc.)
    categoryMeta[categoryId] = {
      id: category.id,
      sort: category.sort,
      image: category.image,
      alcohol: category.alcohol || 0,
      sizeVariants: category.sizeVariants || category.size_variants || []
    }
    
    // Transform items - API might use 'items' or 'courses'
    const itemsArray = category.items || category.courses || []
    items[categoryId] = itemsArray
      .filter(item => {
        // Filter out items without prices (offers/promotions that can't be ordered)
        if (!item.prices || item.prices.length === 0) {
          return false
        }
        return true
      })
      .map(item => {
        try {
          return transformMenuItem(item, category)
        } catch (err) {
          console.error('[API] Error transforming item:', item.name, err)
          return null
        }
      })
      .filter(Boolean) // Remove any null items from failed transforms
  }

  return { items, categories, categoryMeta }
}

/**
 * Transform a single menu item from API format to Vue format
 * @param {Object} apiItem - Item from Laravel API
 * @param {Object} category - Parent category with size variants
 * @returns {Object} Transformed menu item
 */
function transformMenuItem(apiItem, category) {
  // Safety check for prices
  const prices = apiItem.prices || []
  const basePrice = prices[0] || 0
  
  const transformed = {
    id: `item_${apiItem.id}`,
    laravelId: apiItem.id, // Keep original ID for order submission
    name: apiItem.name,
    description: apiItem.description || '',
    price: basePrice, // Base price (first variant) in ØRE
    image: apiItem.image || getPlaceholderImage(category.name),
    discount: apiItem.discount || 0,
    labels: apiItem.labels || [],
    customizations: {}
  }

  // Handle size variants (from category's sizeVariants + item's prices)
  if (category.sizeVariants && category.sizeVariants.length > 1 && prices.length > 1) {
    transformed.customizations.sizes = category.sizeVariants.map((variantName, index) => ({
      id: `size_${index + 1}`,
      nameKey: variantName, // Will need i18n or direct display
      name: variantName,
      priceModifier: (prices[index] || prices[0]) - prices[0], // Difference from base
      absolutePrice: prices[index] || prices[0] // Store absolute price too
    }))
  }

  // Handle choice groups (radio/checkbox customizations)
  if (apiItem.choiceGroups && apiItem.choiceGroups.length > 0) {
    for (const group of apiItem.choiceGroups) {
      // Determine the customization type based on group name/type
      const groupKey = getCustomizationKey(group)
      
      transformed.customizations[groupKey] = group.options.map(opt => ({
        id: `opt_${opt.id}`,
        laravelId: opt.id,
        ingredientId: opt.ingredientId,
        nameKey: opt.name, // Name from ingredient
        name: opt.name,
        priceModifier: opt.price || 0 // Already in ØRE
      }))
      
      // Store group metadata
      if (!transformed.customizations._groups) {
        transformed.customizations._groups = []
      }
      transformed.customizations._groups.push({
        id: group.id,
        name: group.name,
        key: groupKey,
        type: group.type, // 'radio' or 'checkbox'
        required: group.required || false
      })
    }
  }

  // Handle removable ingredients
  if (apiItem.removableIngredients && apiItem.removableIngredients.length > 0) {
    transformed.customizations.removeIngredients = apiItem.removableIngredients.map(ing => ({
      id: `rem_${ing.id}`,
      ingredientId: ing.id,
      nameKey: ing.name,
      name: ing.name
    }))
  }

  // Handle addable ingredients (extras)
  if (apiItem.addableIngredients && apiItem.addableIngredients.length > 0) {
    transformed.customizations.addIngredients = apiItem.addableIngredients.map(ing => ({
      id: `add_${ing.id}`,
      ingredientId: ing.id,
      nameKey: ing.name,
      name: ing.name,
      price: ing.price || 0 // Already in ØRE
    }))
  }

  // Clean up empty customizations
  if (Object.keys(transformed.customizations).length === 0 || 
      (Object.keys(transformed.customizations).length === 1 && transformed.customizations._groups)) {
    delete transformed.customizations
  }

  return transformed
}

/**
 * Determine the customization key based on choice group name
 * Maps Laravel choice group names to Vue customization types
 */
function getCustomizationKey(group) {
  const nameLower = group.name.toLowerCase()
  
  if (nameLower.includes('ost') || nameLower.includes('cheese')) {
    return 'cheese'
  }
  if (nameLower.includes('saus') || nameLower.includes('sauce') || nameLower.includes('dip')) {
    return 'sauces'
  }
  if (nameLower.includes('størrelse') || nameLower.includes('size')) {
    return 'sizes'
  }
  if (nameLower.includes('drikke') || nameLower.includes('drink')) {
    return 'drinks'
  }
  
  // Default: use sanitized group name as key
  return `group_${group.id}`
}

/**
 * Get placeholder image for items without images
 */
function getPlaceholderImage(categoryName) {
  const categoryLower = (categoryName || '').toLowerCase()
  
  // Default food images by category
  const placeholders = {
    burger: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=800&q=80',
    pizza: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=800&q=80',
    salat: 'https://images.unsplash.com/photo-1550304943-4f24f54ddde9?auto=format&fit=crop&w=800&q=80',
    dessert: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?auto=format&fit=crop&w=800&q=80',
    drikke: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?auto=format&fit=crop&w=800&q=80',
    default: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80'
  }

  for (const [key, url] of Object.entries(placeholders)) {
    if (categoryLower.includes(key)) {
      return url
    }
  }
  
  return placeholders.default
}

/**
 * Cache helper - store menu with timestamp
 */
export function cacheMenu(slug, data) {
  const cacheKey = `dandero_menu_${slug}`
  localStorage.setItem(cacheKey, JSON.stringify({
    data,
    timestamp: Date.now()
  }))
}

/**
 * Cache helper - get cached menu if not stale
 * @param {string} slug - Restaurant slug
 * @param {number} maxAge - Max cache age in ms (default 5 minutes)
 */
export function getCachedMenu(slug, maxAge = 5 * 60 * 1000) {
  const cacheKey = `dandero_menu_${slug}`
  const cached = localStorage.getItem(cacheKey)
  
  if (!cached) return null
  
  try {
    const { data, timestamp } = JSON.parse(cached)
    if (Date.now() - timestamp < maxAge) {
      return data
    }
  } catch (e) {
    localStorage.removeItem(cacheKey)
  }
  
  return null
}

/**
 * Clear menu cache for a restaurant
 */
export function clearMenuCache(slug) {
  localStorage.removeItem(`dandero_menu_${slug}`)
}
